<template>
  <FusionBaseDialog
    :open="open"
    :title="dialogTitle"
    persistent-overlay
    padded
    data-cy="addWidgetsDialog"
  >
    <template v-slot:header>
      <v-card-item>
        <template v-slot:prepend>
          <v-icon icon="mdi-view-dashboard" />
          <v-icon v-if="!editing" icon="mdi-plus" size="x-small" class="append-plus" />
        </template>
        <v-card-title>
          {{ dialogTitle }}
        </v-card-title>
        <v-card-subtitle>
          {{ $t('dashboard.addWidgetsDialog.subtitle') }}
        </v-card-subtitle>
      </v-card-item>
    </template>
    <v-card-text class="px-0">
      <v-row no-gutters v-if="!editing">
        <v-col cols="12">
          <v-select
            v-model="selectedWidget"
            :items="widgetOptions"
            :rules="[widgetsRemainingValid]"
            :label="$t('dashboard.addWidgetsDialog.widgetType')"
            @update:modelValue="getWidgetsRemaining()"
            class="mb-4"
            data-cy="widgetTypeDropdown"
          ></v-select>
        </v-col>
      </v-row>
      <v-row no-gutters>
        <v-col cols="12">
          <v-autocomplete
            v-model="selectedDeviceId"
            :multiple="allowMultipleDevices"
            :clearable="allowMultipleDevices"
            :items="selectOptions"
            :label="$t('dashboard.addWidgetsDialog.device.label')"
            :no-data-text="'dashboard.addWidgetsDialog.device.noDataText'"
            :hint="
              allowMultipleDevices
                ? $t('dashboard.addWidgetsDialog.device.hintMultipleDevices', {
                    qty: maxDeviceSelect
                  })
                : undefined
            "
            :persistent-hint="allowMultipleDevices"
            :disabled="disabledDeviceSelect"
            :filter-keys="['raw.title', 'raw.subtitle']"
            data-cy="widgetDeviceDropdown"
          >
            <template v-slot:selection="{ item, index }">
              <div v-if="!allowMultipleDevices" class="device-selections">
                {{ item.raw.text }}
              </div>
              <div
                v-if="allowMultipleDevices && selectedDeviceIds.length === 1"
                class="device-selections"
              >
                {{ item.raw.text }}
              </div>
              <div
                v-if="allowMultipleDevices && selectedDeviceIds.length > 1 && index === 0"
                class="device-selections"
              >
                {{
                  $t('dashboard.addWidgetsDialog.device.qtyDevicesSelected', {
                    qty: selectedDeviceIds.length
                  })
                }}
              </div>
            </template>
            <template v-slot:item="{ item, props }">
              <v-list-item v-bind="{ ...props, title: undefined }" lines="two">
                <template v-if="allowMultipleDevices" v-slot:prepend="{ isSelected }">
                  <v-list-item-action start>
                    <v-checkbox-btn
                      :ripple="false"
                      :model-value="isSelected"
                      :disabled="disableDeviceSelectItem(item)"
                    />
                  </v-list-item-action>
                </template>
                <v-list-item-title class="select-item__title">
                  <span>{{ item.title }}</span>
                </v-list-item-title>
                <v-list-item-subtitle>{{ item.raw?.subtitle }}</v-list-item-subtitle>
              </v-list-item>
            </template>
          </v-autocomplete>
        </v-col>
      </v-row>
    </v-card-text>
    <template v-slot:actions>
      <v-btn
        @click="close"
        variant="outlined"
        class="text-black"
        data-cy="addWidgetsCancelButton"
        >{{ $t('buttons.cancel') }}</v-btn
      >
      <v-spacer></v-spacer>
      <v-btn
        @click="save"
        :disabled="saveDisabled"
        class="bg-primary text-white"
        data-cy="addWidgetsDoneButton"
        >{{ $t('buttons.done') }}</v-btn
      >
    </template>
  </FusionBaseDialog>
</template>

<script lang="ts">
import { defineComponent, nextTick, type PropType } from 'vue'
import { deviceModelDisplay, deviceSubmodelDisplay } from '@/services/map'
import { getPermissionsByDeviceId } from '@/services/subscriptions'
import { SlugsEnum } from '@/permissions/SlugsEnum'
import { getUserAccountId } from '@/helpers/loginas/logInAsHelper'
import { NAMESPACES } from '@/store/types'
import { DEVICE_GETTERS } from '@/store/devices'
import { DEVICE_MODELS_GETTERS } from '@/store/devicemodels'
import type { DashboardWidget, WidgetComponentKey } from '@/views/dashboard.vue'

const MAX_WIDGETS_PER_TYPE = 5
const MAX_WIDGETS_BY_TYPE: Record<WidgetComponentKey, number> = {
  MapWidget: 30,
  HistoryWidget: 10,
  PopoverWidget: 30
} as const

interface WidgetOption {
  title: string
  value: WidgetComponentKey
}

interface DeviceOption {
  deviceId: string
  name: string
  model: string
  submodel: string
}

interface SelectOption {
  text: string
  title: string
  value: string
  subtitle: string
}

interface NewWidget {
  widgetComponent: WidgetComponentKey
  deviceIds: string[]
}

interface ConfiguredWidget {
  widgetId: string
  deviceIds: string[]
  previousDeviceIds: string[]
}

export default defineComponent({
  name: 'AddWidgetsDialog',
  props: {
    deviceIds: {
      type: Array as PropType<string[]>,
      default: (): string[] => []
    },
    widgetId: {
      type: String,
      default: null
    },
    open: Boolean,
    widgets: {
      type: Array as PropType<DashboardWidget[]>,
      default: (): DashboardWidget[] => []
    },
    widgetType: {
      type: String as PropType<WidgetComponentKey>,
      default: null
    },
    widgetMaxDevices: {
      type: Object as PropType<Record<WidgetComponentKey, number>>,
      default: () => ({})
    },
    editing: { type: Boolean, default: false }
  },
  data() {
    return {
      accountId: null as string | null,
      selectedDeviceId: null as string | string[] | null,
      selectedWidget: null as WidgetComponentKey | null,
      widgetsRemaining: MAX_WIDGETS_PER_TYPE,
      widgetOptions: [
        {
          title: this.$t('dashboard.addWidgetsDialog.widgetTypes.map') as string,
          value: 'MapWidget'
        },
        {
          title: this.$t('dashboard.addWidgetsDialog.widgetTypes.history') as string,
          value: 'HistoryWidget'
        },
        {
          title: this.$t('dashboard.addWidgetsDialog.widgetTypes.popover') as string,
          value: 'PopoverWidget'
        }
      ] as WidgetOption[],
      devices: [] as DeviceOption[]
    }
  },
  computed: {
    selectOptions(): SelectOption[] {
      return this.devices
        .filter((device) => this.allowDeviceWidget(device?.deviceId))
        .map((device) => {
          const { deviceId, name, model, submodel } = device
          const modelKey =
            this.$store.getters[
              `${NAMESPACES.DEVICE_MODELS}/${DEVICE_MODELS_GETTERS.GET_MODEL_KEY}`
            ](model)
          const modelName = deviceModelDisplay(modelKey, this.$t.bind(this))
          const submodelKey = this.$store.getters[
            `${NAMESPACES.DEVICE_MODELS}/${DEVICE_MODELS_GETTERS.GET_SUBMODEL_KEY}`
          ](model, submodel)
          const submodelName = deviceSubmodelDisplay(submodelKey, this.$t.bind(this))
          const subtitle = submodel ? `${modelName} (${submodelName})` : modelName
          return {
            text: `${name} | ${subtitle}`,
            title: name,
            subtitle,
            value: deviceId
          }
        })
    },
    maxDeviceSelect(): number {
      if (!this.selectedWidget) return 0
      return this.widgetMaxDevices?.[this.selectedWidget] || 0
    },
    allowMultipleDevices(): boolean {
      return this.maxDeviceSelect > 1
    },
    disabledDeviceSelect(): boolean {
      return !this.selectedWidget
    },
    saveDisabled(): boolean {
      if (this.editing) {
        return this.selectedDeviceIds.length === 0
      }
      return (
        this.selectedDeviceIds.length === 0 ||
        this.selectedWidget === null ||
        this.widgetsRemaining <= 0
      )
    },
    selectedDeviceIds(): string[] {
      if (Array.isArray(this.selectedDeviceId)) {
        const deviceIds = [...this.selectedDeviceId]
        const order = this.devices.map((device) => device.deviceId)
        deviceIds.sort((a, b) => order.indexOf(a) - order.indexOf(b))
        deviceIds.splice(this.maxDeviceSelect, this.devices.length)
        return deviceIds
      } else if (this.selectedDeviceId !== null) {
        return [this.selectedDeviceId]
      }
      return []
    },
    newWidget(): NewWidget | Record<string, never> {
      if (this.selectedWidget !== null && this.selectedDeviceId !== null) {
        return {
          widgetComponent: this.selectedWidget,
          deviceIds: this.selectedDeviceIds
        }
      }
      return {}
    },
    configuredWidget(): ConfiguredWidget | Record<string, never> {
      if (this.widgetId !== null && this.selectedDeviceId !== null) {
        return {
          widgetId: this.widgetId,
          deviceIds: this.selectedDeviceIds,
          previousDeviceIds: this.deviceIds
        }
      }
      return {}
    },
    dialogTitle(): string {
      return this.editing
        ? (this.$t('dashboard.addWidgetsDialog.titleEditing') as string)
        : (this.$t('dashboard.addWidgetsDialog.title') as string)
    },
    widgetsRemainingValid(): boolean | string {
      return (
        this.widgetsRemaining > 0 || (this.$t('dashboard.addWidgetsDialog.maxWidgetType') as string)
      )
    }
  },
  watch: {
    async open(): Promise<void> {
      if (this.open) {
        this.widgetsRemaining = MAX_WIDGETS_PER_TYPE
        this.getDevices()
        if (this.editing) {
          this.selectedWidget = this.widgetType
          await nextTick()
          this.selectedDeviceId = this.getSelectedDeviceId()
        }
      }
    },
    selectedWidget(newVal: WidgetComponentKey | null, oldVal: WidgetComponentKey | null): void {
      if (newVal !== oldVal) {
        this.selectedDeviceId = null
      }
    }
  },
  async mounted(): Promise<void> {
    this.accountId = await getUserAccountId(this.$auth)
  },
  methods: {
    close(): void {
      this.$emit('close')
      this.selectedDeviceId = null
      this.selectedWidget = null
    },
    save(): void {
      if (this.devices.length > 0) {
        if (this.editing) {
          const widget = JSON.parse(JSON.stringify(this.configuredWidget))
          this.$emit('widgetUpdated', widget)
        } else {
          const widget = JSON.parse(JSON.stringify(this.newWidget))
          this.$emit('widgetAdded', widget)
        }
      }
      this.close()
    },
    allowDeviceWidget(deviceId: string): boolean {
      const deviceSlugPermissions = getPermissionsByDeviceId(deviceId) || []
      const hasDashboardWidgetCreate = deviceSlugPermissions.includes(
        SlugsEnum.DashboardCreateWidget
      )
      if (!hasDashboardWidgetCreate) {
        return false
      }
      if (this.selectedWidget === 'MapWidget') {
        const deviceModel =
          this.$store.getters[`${NAMESPACES.DEVICES}/${DEVICE_GETTERS.GET_MODEL_BY_DEVICE_ID}`](
            deviceId
          )
        const showMapSettings =
          this.$store.getters[
            `${NAMESPACES.DEVICE_MODELS}/${DEVICE_MODELS_GETTERS.GET_SHOW_MAP_SETTINGS}`
          ](deviceModel)
        return !!showMapSettings
      }
      return true
    },
    getDevices(): void {
      const devices: DeviceOption[] = []
      const deviceNames: Record<string, string> = this.$store.state.devices.name
      for (const key in deviceNames) {
        if (this.isDeviceSharedOrOwned(key)) {
          devices.push({
            deviceId: key,
            name: this.$store.getters[
              `${NAMESPACES.DEVICES}/${DEVICE_GETTERS.GET_NAME_BY_DEVICE_ID}`
            ](key),
            model:
              this.$store.getters[`${NAMESPACES.DEVICES}/${DEVICE_GETTERS.GET_MODEL_BY_DEVICE_ID}`](
                key
              ),
            submodel:
              this.$store.getters[
                `${NAMESPACES.DEVICES}/${DEVICE_GETTERS.GET_SUBMODEL_BY_DEVICE_ID}`
              ](key)
          })
        }
      }
      devices.sort((a, b) => (a.name > b.name ? 1 : -1))
      this.devices = devices
    },
    getWidgetsRemaining(): void {
      let widgetsRemaining = MAX_WIDGETS_PER_TYPE

      switch (this.selectedWidget) {
        case 'PopoverWidget':
        case 'MapWidget':
        case 'HistoryWidget':
          widgetsRemaining = MAX_WIDGETS_BY_TYPE[this.selectedWidget]
          break
      }

      this.widgets.forEach((widget) => {
        if (widget.widgetComponent === this.selectedWidget) {
          widgetsRemaining--
        }
      })
      this.widgetsRemaining = widgetsRemaining
    },
    getSelectedDeviceId(): string | string[] | null {
      const availableDeviceIds = new Set(this.devices.map((device) => device.deviceId))
      if (this.allowMultipleDevices) {
        return this.deviceIds.filter((deviceId) => availableDeviceIds.has(deviceId))
      } else {
        const deviceId = this.deviceIds?.[0]
        return availableDeviceIds.has(deviceId) ? deviceId : null
      }
    },
    disableDeviceSelectItem(item: { value: string }): boolean {
      if (
        this.allowMultipleDevices &&
        Array.isArray(this.selectedDeviceId) &&
        this.selectedDeviceId.length === this.maxDeviceSelect &&
        !this.selectedDeviceId.includes(item.value)
      ) {
        return true
      }
      return false
    },
    isDeviceSharedOrOwned(deviceId: string): boolean {
      const isOwned =
        this.accountId ===
        this.$store.getters[`${NAMESPACES.DEVICES}/${DEVICE_GETTERS.GET_ACCOUNT_BY_DEVICE_ID}`](
          deviceId
        )
      const isShared =
        this.$store.getters[`${NAMESPACES.DEVICES}/${DEVICE_GETTERS.GET_IS_SHARED_BY_DEVICE_ID}`](
          deviceId
        )
      return isOwned || isShared
    }
  }
})
</script>

<style lang="scss" scoped>
.append-plus {
  align-self: flex-start;
  margin-left: -0.25em;
  margin-right: -0.75em;
}
.device-selections {
  flex-basis: 100%;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.select-item__title {
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  white-space: normal;
  overflow: hidden;
  text-overflow: ellipsis;
}
</style>
